import { useQuery } from "@apollo/client";
import {
  Card,
  Clickable,
  Heading,
  media,
  Message,
  modularScale,
  palette,
  Paragraph,
  pxToRem,
  Share,
  Spacing,
  Text,
} from "@ottatech/design";
import { Spinner } from "@ottatech/shared-components";
import styled from "styled-components";
import { Navigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { captureException } from "@sentry/browser";
import { toast, Id } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { pushAnalyticsEvent } from "@ottatech/analytics";

import { WhatsAppLogo } from "./WhatsAppLogo";

import { CurrentUserDocument } from "schema";

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${modularScale()};
  padding-top: ${modularScale(5)};
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: stretch;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
`;

const ExternalShareButton = styled.a`
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  gap: 4px;
  padding: 12px 16px;
  font-weight: 700;
  color: ${palette.brand.black};
  text-decoration: none;
`;

const WhatsApp = styled(ExternalShareButton)`
  background-color: #25d366;
  color: #fff;
  font-family: sans-serif;
`;

const EmailButton = styled(ExternalShareButton)`
  background-color: ${palette.brand.yellow};
`;

const LinkBox = styled.div`
  flex: 1;
  padding: ${modularScale(-2)};
  display: flex;
  align-items: center;
  background: ${palette.brand.white};
  transition: background 200ms ease-in-out;
  border-radius: 4px;
  border: 1px solid ${palette.grayscale.shade900};
`;

const ShareButton = styled(ExternalShareButton)`
  background-color: ${palette.brand.yellow};
`;

const Steps = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: ${modularScale()};

  ${media({ from: "tablet" })`
    flex-direction: row;
  `}
`;

const Step = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${modularScale()};
  text-align: left;
  ${media({ from: "tablet" })`
    flex-direction: column;
    align-items: center;
    text-align: center;
  `}
`;

function ShareLink({ id, onShare }: { id: string; onShare(): Promise<void> }) {
  const handleClick = () => {
    onShare()
      .then(() => {
        pushAnalyticsEvent({
          eventName: "Candidate Shared",
          name: "Referral Link",
        });
      })
      .catch(captureException);
  };

  return (
    <ButtonsContainer>
      <LinkBox>
        <Text style={{ flex: 1 }}>app.otta.com/join/{id}</Text>
      </LinkBox>
      <ShareButton
        as={Clickable}
        data-analytics-id="refer-share-link"
        onClick={handleClick}
      >
        <Share />
        <Text bold style={{ marginLeft: modularScale(-5) }}>
          Share
        </Text>
      </ShareButton>
    </ButtonsContainer>
  );
}

function ShareButtons({
  id,
  url,
  onCopy,
}: {
  id: string;
  url: string;
  onCopy(): void;
}) {
  const body = `Hey! I think you'd love Otta. Stay up to date with the latest and best jobs in tech with personalised job matches straight to your inbox. Click to sign up: ${url}`;
  return (
    <ButtonsContainer>
      <LinkBox
        as={Clickable}
        data-analytics-id="refer-copy-link"
        onClick={onCopy}
      >
        <Text style={{ flex: 1 }}>app.otta.com/join/{id}</Text>
      </LinkBox>

      <Buttons>
        <EmailButton
          data-analytics-id="refer-email-link"
          href={`mailto:?subject=${encodeURIComponent(
            "You're invited to check out Otta"
          )}&body=${encodeURIComponent(body)}`}
        >
          <Message />
          Email
        </EmailButton>
        <WhatsApp
          data-analytics-id="refer-whatsapp-link"
          href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
            body
          )}`}
        >
          <WhatsAppLogo width={20} height={20} fill={palette.brand.white} />
          WhatsApp
        </WhatsApp>
      </Buttons>
    </ButtonsContainer>
  );
}

export function Referral(): React.ReactElement {
  const { data, loading } = useQuery(CurrentUserDocument);

  const toastId = useRef<Id>();

  useEffect(() => {
    if (data?.currentUser) {
      pushAnalyticsEvent({
        eventName: "Candidate Viewed",
        name: "Referral Page",
      });
    }
  }, [data?.currentUser]);

  if (loading) {
    return <Spinner />;
  }

  if (!data?.currentUser) {
    return <Navigate to="/" replace />;
  }

  const shareData = {
    url: `https://app.otta.com/join/${data.currentUser.externalId}`,
    text: "Hey! I think you'd love Otta. Stay up to date with the latest and best jobs in tech with personalised job matches straight to your inbox.",
    title: "You're invited to check out Otta",
  };

  const onCopy = () => {
    navigator.clipboard
      .writeText(shareData.url)
      .then(() => {
        toast.dismiss(toastId.current);
        toastId.current = toast.dark("URL copied to clipboard", {
          autoClose: 4000,
        });
      })
      .catch(captureException);
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Container>
        <div style={{ maxWidth: pxToRem(800) }}>
          <Card>
            <Spacing size={2}>
              <Spacing>
                <Heading size={4}>
                  {data.currentUser.firstName}, refer a friend and get £10
                </Heading>
                <Paragraph>
                  Tell your friends about Otta and we&apos;ll reward you with
                  £10 for every successful sign up*.
                </Paragraph>
              </Spacing>

              {typeof navigator.share === "function" ? (
                <ShareLink
                  id={data.currentUser.externalId}
                  onShare={() => navigator.share!(shareData)}
                />
              ) : (
                <ShareButtons
                  id={data.currentUser.externalId}
                  url={shareData.url}
                  onCopy={onCopy}
                />
              )}

              <Steps>
                <Step>
                  <Heading size={2}>1</Heading>
                  <Text>
                    Share your referral link with your friends who work in tech
                  </Text>
                </Step>
                <Step>
                  <Heading size={2}>2</Heading>
                  <Text>
                    Your friends will need to sign up using your link and
                    complete their preferences by uploading their LinkedIn URL
                  </Text>
                </Step>
                <Step>
                  <Heading size={2}>3</Heading>
                  <Text>
                    For every successful sign-up, we&apos;ll send you £10 Amazon
                    voucher as a thank you
                  </Text>
                </Step>
              </Steps>

              <Text size={-1}>
                *See the full Ts & Cs{" "}
                <a
                  target="_blank"
                  href="https://otta.com/terms-and-conditions/referrals?v=2"
                  rel="noreferrer"
                >
                  here
                </a>
              </Text>
            </Spacing>
          </Card>
        </div>
      </Container>
    </>
  );
}
